const liveShoppingShareTiktokShopContent = `
      <p><strong>The short version:</strong> live shopping is only about 8% of US TikTok Shop sales. Video and the shop tab do the rest. Live is not dying. It is just not the main way people buy.</p>

      <p>In 2024, live was 10% of sales. In 2025, it was 14%. In the first half of 2026, it was 8.2%. While that share dropped, TikTok Shop itself more than doubled.</p>

      <p>Today, pre-recorded video accounts for 40.4% of counted sales. The shop tab accounts for 51.4%.</p>

      <p>So if you treat live as your main sales plan, you are betting on the smallest piece. If you treat live as a way to make videos, the plan makes more sense.</p>

      <h2>What the numbers say</h2>

      <p>These numbers come from Momentum Works, using Tabcut data. See their <a href="https://thelowdown.momentum.asia/new-report-tiktok-shop-u-s-gmv-grew-68-to-reach-us15-1b-in-2025/" target="_blank" rel="noopener noreferrer">2025 report</a> (11 February 2026) and their <a href="https://thelowdown.momentum.asia/tiktok-shop-on-track-to-surpass-us100-billion-gmv-globally-in-2026/" target="_blank" rel="noopener noreferrer">first-half 2026 report</a> (6 August 2026).</p>

      <p>GMV just means total sales. &quot;Share&quot; means the slice of those sales that came from live, video, or the shop tab.</p>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Shop tab</th>
              <th>Video</th>
              <th>Live</th>
              <th>US sales</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>2024</td>
              <td>n/a</td>
              <td>n/a</td>
              <td>10%</td>
              <td>$9B</td>
            </tr>
            <tr>
              <td>2025</td>
              <td>36%</td>
              <td>50%</td>
              <td>14%</td>
              <td>$15.1B</td>
            </tr>
            <tr>
              <td>First half of 2026</td>
              <td>51.4%</td>
              <td>40.4%</td>
              <td>8.2%</td>
              <td>$11.8B</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>A smaller slice of a bigger pie is not the same as losing money.</p>

      <p>14% of $15.1 billion is about $2.1 billion from live in 2025. 8.2% of $11.8 billion is $968 million in the first half of 2026. If you double that first half, you get about $1.94 billion for a full year.</p>

      <p>That last step is a rough estimate. The holiday half of the year is usually bigger, so a full year is likely a bit higher than $1.94 billion. Live sales are probably flat or up a little. They are not collapsing.</p>

      <p><strong>Live is still making about the same amount of money. The rest of TikTok Shop grew 103% in the first half of 2026.</strong> The extra sales went to video and the shop tab, not live.</p>

      <p>Another dataset says the same thing. Charm.io numbers, reported by Modern Retail and passed on in eMarketer&apos;s <a href="https://www.emarketer.com/content/faq-on-livestream-commerce--what-marketers-need-know-about-live-shopping-2026" target="_blank" rel="noopener noreferrer">livestream FAQ</a> (16 January 2026), show live&apos;s share of TikTok Shop sales falling from 26% in July 2024 to 18% in July 2025. Different source. Same direction.</p>

      <p>Zoom out and live still has a ceiling. eMarketer says US livestream shopping was $14.64 billion in 2025 and grew about 50%. Only 21.7% of US online buyers have ever bought on a livestream. 43% of US adults have not used it and do not want to.</p>

      <h2>Why live&apos;s share is shrinking</h2>

      <p>Three reasons, starting with the biggest.</p>

      <h3>1. TikTok&apos;s ads now pick video over live</h3>

      <p>Since July 2025, GMV Max has been <a href="https://ads.tiktok.com/resources/help/article/gmv-max-migration-tiktok-shop-ads?lang=en" target="_blank" rel="noopener noreferrer">the only TikTok Shop ad type you can run</a>. The old live, product, and video shopping ads are gone. You cannot switch back to manual bidding.</p>

      <p>GMV Max is TikTok&apos;s automatic ad tool. You set a return target. TikTok spends to hit it. TikTok says it <a href="https://ads.tiktok.com/help/article/about-gmv-max-campaigns-in-tiktok-ads-manager?lang=en" target="_blank" rel="noopener noreferrer">&quot;optimizes for total channel ROI for your TikTok Shop.&quot;</a> In plain English: it spends where it thinks it will get the most sales.</p>

      <p>The live version of that tool <a href="https://ads.tiktok.com/help/article/about-live-gmv-max?lang=en" target="_blank" rel="noopener noreferrer">moves money between video and live on its own</a>. If it keeps sending money to pre-recorded video, that is data, not a vibe. Video is winning the spend because it is winning the sales.</p>

      <h3>2. Live costs more to run</h3>

      <p>A video is made once and can keep selling. A live show needs a host, often a crew, studio time, and ads to fill the room. Sales happen during the show. Then they stop.</p>

      <p>TikTok&apos;s own <a href="https://ads.tiktok.com/help/article/best-practices-for-live-gmv-max" target="_blank" rel="noopener noreferrer">live ad tips</a> ask for sessions of three hours or more. They also say more than eight hours a day is &quot;for greater results.&quot; They say your budget should be &quot;two to three times higher than your historical LIVE ads.&quot; That is a full work shift plus extra ad spend, for one selling window.</p>

      <h3>3. The shop tab took the growth</h3>

      <p>From 2025 to the first half of 2026, the shop tab went from 36% to 51.4% of counted sales. People are searching and browsing, the way they do on Amazon. It is not flashy. It is now the biggest piece.</p>

      <h2>What live is actually good at</h2>

      <p>Skip the talk about &quot;authenticity.&quot; Look at what TikTok asks you to do.</p>

      <p>In those same <a href="https://ads.tiktok.com/help/article/best-practices-for-live-gmv-max" target="_blank" rel="noopener noreferrer">live ad tips</a>, TikTok wants <strong>&quot;at least 50–70 videos&quot;</strong> to test, <strong>or 5–10 new videos every day</strong>.</p>

      <p>That is a lot of video. Not a month. A day.</p>

      <p>That is the real bottleneck. You do not mainly have an ads problem. You have a &quot;we need more videos&quot; problem. A three-hour live with a host showing products, answering questions, and reacting to chat creates more raw footage than a scripted shoot. It is messy, continuous, and already in TikTok&apos;s style.</p>

      <p><strong>The best reason to go live is to make video cheaply, at the volume TikTok wants.</strong> Live can sell too. Selling is the smaller win.</p>

      <h3>The catch</h3>

      <p>TikTok will <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=6769650398660398&amp;lang=en" target="_blank" rel="noopener noreferrer">auto-cut clips from a live</a>. The cap is tight: <strong>five videos per live, each under 60 seconds, each about one product.</strong> It takes about five minutes to generate.</p>

      <p>If you stream eight hours, that is five minutes of auto clips from 480 minutes of show. Under 1%.</p>

      <p>So you cannot rely on TikTok&apos;s auto-clip tool. You need a person who cuts 5–10 usable videos a day from the footage. That is a real job. If your live budget does not include that person, you are paying for a sales show and getting sales-show results.</p>

      <h2>A simple test before you spend</h2>

      <p>Do this in one afternoon. Use your numbers, not the US averages above.</p>

      <ol>
        <li>
          <strong>See where your sales actually come from.</strong> Beauty, jewelry, resale, and collectibles often do better on live than home goods. If live is clearly more than 8% of your counted sales, you may be an exception. Find out why before you change course.
        </li>
        <li>
          <strong>Divide live sales by hours streamed.</strong> That is sales per live hour. Glossy <a href="https://www.glossy.co/pop/tiktok-live-shopping-is-proving-lucrative-for-beauty-brands-across-categories/" target="_blank" rel="noopener noreferrer">reported in October 2025</a> that GoPure made $1 million from 483 hours of TikTok Live in 2025, about $2,070 an hour. GoPure already had a big creator program. The number comes from their marketing lead. It is not audited. Treat it as a high mark for a strong beauty brand, not a goal you should expect.
        </li>
        <li>
          <strong>Count the videos you actually got.</strong> How many usable clips did last month&apos;s lives produce, and who cut them? If the answer is &quot;the five TikTok made for us,&quot; you are using live as a sales channel.
        </li>
      </ol>

      <p>Then use this rule:</p>

      <ul>
        <li><strong>Live is under 10% of your sales, and you get fewer than 20 usable videos a month.</strong> The show is expensive and not helping enough. Hire an editor, or stop.</li>
        <li><strong>Live is under 10% of sales, but your video sales are growing.</strong> Live is working as a content studio. Budget it that way. Stop arguing about the show&apos;s ROI.</li>
        <li><strong>Live is over 20% of your sales.</strong> Live really sells in your category. Keep going and ignore most of this article.</li>
      </ul>

      <h2>What to track instead of &quot;how much did this show make?&quot;</h2>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>What to track</th>
              <th>Why it is more honest</th>
              <th>Where it comes from</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Usable videos per live</td>
              <td>Ads do not get credit for this</td>
              <td>Your editor</td>
            </tr>
            <tr>
              <td>Cost per usable video</td>
              <td>Lets you compare live to a normal shoot</td>
              <td>Show cost ÷ videos made</td>
            </tr>
            <tr>
              <td>Average watch time</td>
              <td>Tells you if the host is holding people</td>
              <td>TikTok Live analytics</td>
            </tr>
            <tr>
              <td>Click-to-order rate</td>
              <td>Tells you if the offer and demo work</td>
              <td>TikTok Live analytics</td>
            </tr>
            <tr>
              <td>Sales per live hour</td>
              <td>Easy to compare across shows</td>
              <td>Live sales ÷ hours</td>
            </tr>
            <tr>
              <td>A week with live vs. a week without</td>
              <td>The only clean way to see what live added</td>
              <td>Your own test</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>That last test matters most, and almost nobody runs it. Keep other spend the same. Compare a week with live to a week without it.</p>

      <h2>This does not mean &quot;stop going live&quot;</h2>

      <p>Live still works. Three things can be true at once.</p>

      <p><strong>It works in some categories.</strong> Collectibles, trading cards, resale, jewelry, and beauty demos still do well. People want to see the item. Whatnot said it did $8 billion in live sales in 2025, almost all in those kinds of categories.</p>

      <p><strong>It works on big days.</strong> Glossy said P.Louise did $2.7 million in 14 hours on a holiday pre-launch (Glossy got that number second-hand). GoPure did $100,000 in one eight-hour launch. That is about six times GoPure&apos;s usual $2,070 an hour. A launch is not the same as a random Tuesday.</p>

      <p><strong>And live is still a smaller slice of TikTok Shop.</strong> The takeaway is not &quot;quit live.&quot; It is: stop judging live only by show sales. Judge it by the videos it creates. Pay someone to cut those videos.</p>

      <p>The brands doing this well are not the ones live the most hours. They are the ones who know what the hours are for.</p>

      <p>For what a session actually costs, see <a href="/blog/live-shopping-cost">what a live shopping stream actually costs</a>. For how to staff the room, see <a href="/blog/live-shopping-host">how to find a vetted live shopping host</a>. For a primer on the format, see <a href="/blog/what-is-live-shopping-why-this-500-billion-market-is-changing-e-commerce-forever">what live shopping is</a>. For how creators get booked, see <a href="/blog/the-creator-s-guide-to-live-shopping-how-to-get-found-get-booked-and-get-results">the creator&apos;s guide to live shopping</a>.</p>
`;

export default liveShoppingShareTiktokShopContent;
